import { Component, computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FinaliseTowDialogComponent } from '../../../finalisation/components/finalise-tow-dialog/finalise-tow-dialog.component';
import { PermissionsService } from '../../../permissions/permissions.service';
import { SamplesService } from '../../../samples/service/samples.service';
import { IRecordingEvent } from '../../interface/recording-event.interface';
import { TowRouteData } from '../../interface/tow-route-data.interface';
import { TowsService } from '../../service/tows.service';

@Component({
  template: `
    @if (routeData.tow(); as tow) {
      <app-header>
        @if (tow.metadata?.status !== 'finalised') {
          <button
            mat-flat-button
            (click)="finaliseTow()"
            [checkPermission]="['recordingEvent', 'finalise']"
          >
            Finalise Tow
          </button>
        }

        <cpr-printout-menu />

        <button mat-icon-button [matMenuTriggerFor]="actionsMenu">
          <mat-icon>more_vert</mat-icon>
        </button>

        <mat-menu #actionsMenu="matMenu">
          @if (tow.metadata?.status !== 'finalised') {
            <a mat-menu-item routerLink="edit">
              <mat-icon>edit</mat-icon>
              Edit Details
            </a>
          }

          @if (routeData.towLog()?.length) {
            <a mat-menu-item routerLink="log">
              <mat-icon>map</mat-icon>
              View Tow Log
            </a>
          } @else {
            <a mat-menu-item routerLink="log">
              <mat-icon>add</mat-icon>
              Add Tow Log
            </a>
          }

          <mat-divider />

          @if (!samplesGenerated()) {
            <button
              mat-menu-item
              [disabled]="generateDisabledReason() | async"
              [matTooltip]="generateDisabledReason() | async"
              (click)="onGenerate()"
            >
              <mat-icon>calculate</mat-icon>
              Generate cutting points and samples
            </button>
          } @else {
            <button
              mat-menu-item
              style="color: var(--warn-color)"
              [disabled]="clearDisabledReason() | async"
              [matTooltip]="clearDisabledReason() | async"
              (click)="onClear()"
            >
              <mat-icon style="color: var(--warn-color)">undo</mat-icon>
              Clear cutting points and samples
            </button>

            <a mat-menu-item routerLink="cutting-points">
              <mat-icon>content_cut</mat-icon>
              View Cutting Points
            </a>

            <a mat-menu-item routerLink="samples/allocate">
              <mat-icon>checklist</mat-icon>
              Set PCI Colour &amp; Allocate Samples
            </a>

            <a mat-menu-item routerLink="samples">
              <mat-icon>grid_on</mat-icon>
              View Analysis Overview
            </a>
          }

          <mat-divider />

          <button
            mat-menu-item
            style="color: var(--warn-color)"
            [disabled]="hasSamples() | async"
            [matTooltip]="(hasSamples() | async) ? 'Cannot delete a tow that has samples' : null"
            (click)="onDelete()"
          >
            <mat-icon style="color: var(--warn-color)">delete</mat-icon>
            Delete Tow
          </button>
        </mat-menu>
      </app-header>

      <div style="display: flex; gap: 24px; margin: 24px">
        <app-tow-info-tile [tow]="tow" style="flex-grow: 1" />

        <div
          class="mat-mdc-card"
          style="display: block; overflow: hidden; height: 324px; width: 400px"
        >
          @if (routeData.towLog()?.length) {
            <app-tow-log-map-tile [tow]="tow">
              <a mat-raised-button routerLink="log">View Tow Log</a>
            </app-tow-log-map-tile>
          } @else {
            <div style="height: 100%; display: flex; justify-content: center; align-items: center">
              <a mat-flat-button routerLink="log">Add Tow Log</a>
            </div>
          }
        </div>
      </div>

      <app-tow-samples-tile style="margin: 24px" mode="simple" [tow]="tow" />
    }
  `,
})
export class TowOverviewScreenComponent {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly samplesService = inject(SamplesService);
  private readonly towService = inject(TowsService);

  protected readonly routeData = inject(ActivatedRoute).snapshot.data as TowRouteData;

  protected readonly samplesGenerated = computed(() => !!this.routeData.samples().length);

  protected readonly hasSamples = computed(() =>
    this.samplesService
      .getPaginated('', { simpleFilters: { towId: `ObjectId:${this.routeData.tow()._id}` } })
      .then((samples) => !!samples.count),
  );

  protected readonly generateDisabledReason = computed<Promise<string | undefined>>(async () => {
    if (!this.routeData.towLog()?.length) {
      return 'Tow log has not been entered.';
    }
    if (!(await this.permissionsService.hasPermission('sample', 'generate'))) {
      return 'You lack the sample generate permission.';
    }
    return undefined;
  });

  protected readonly clearDisabledReason = computed<Promise<string | undefined>>(async () => {
    const samples = this.routeData.samples();
    if (samples.some((s) => s.userId || s.isFinalised)) {
      return 'Analysis has already started on this tow.';
    }
    if (!(await this.permissionsService.hasPermission('sample', 'generate'))) {
      return 'You lack the sample generate permission.';
    }
    return undefined;
  });

  onGenerate() {
    const { _id } = this.routeData.tow();
    this.samplesService.generateWithDialog(_id, async () => {
      await this.routeData.samples.refresh();
      await this.router.navigate(['/tows', _id, 'samples', 'allocate']);
    });
  }

  onClear() {
    const { _id } = this.routeData.tow();
    this.samplesService.clearWithDialog(_id, () => {
      location.reload();
    });
  }

  onDelete() {
    this.towService.delete(this.routeData.tow()._id, () => this.router.navigate(['/tows']));
  }

  finaliseTow() {
    const tow = this.routeData.tow();
    this.dialog.open(FinaliseTowDialogComponent, {
      width: '1024px',
      maxWidth: '95vw',
      minHeight: '768px',
      data: {
        towId: tow._id,
        tow,
        onConfirm: () => {
          (tow as IRecordingEvent<'tow'>).metadata.status = 'finalised';
        },
      },
    });
  }
}
